/**
 * KnowledgePage.jsx — Safe Zap Fase 5
 * Internal knowledge base for the team and AI suggestion context.
 */
import React, { useEffect, useState } from "react";
import { Icon } from "../shared/components";
import { formatTime } from "../shared/utils";

const CATEGORY_OPTIONS = [
  { value: "geral", label: "Geral" },
  { value: "produto", label: "Produto" },
  { value: "servico", label: "Serviço" },
  { value: "preco", label: "Preço" },
  { value: "suporte", label: "Suporte" },
  { value: "politica", label: "Política" },
  { value: "processo", label: "Processo" },
  { value: "outro", label: "Outro" },
];

function CategoryBadge({ category }) {
  return <span className="knowledgeCategoryBadge">{category || "geral"}</span>;
}

function KnowledgePage({ api }) {
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null); // article id being viewed/edited
  const [creating, setCreating] = useState(false);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [notice, setNotice] = useState("");
  const [saving, setSaving] = useState(false);

  // Form state (create + edit)
  const [form, setForm] = useState({ title: "", content: "", category: "geral", tags: "", active: true });

  async function load() {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set("q", search);
      if (categoryFilter) params.set("category", categoryFilter);
      const [artRes, catRes] = await Promise.all([
        api.get(`/knowledge?${params}`),
        api.get("/knowledge-categories"),
      ]);
      setArticles(artRes.data || []);
      setCategories(catRes.data || []);
    } catch {}
    finally { setLoading(false); }
  }

  useEffect(() => { load(); }, [search, categoryFilter]);

  function openCreate() {
    setSelected(null);
    setForm({ title: "", content: "", category: "geral", tags: "", active: true });
    setCreating(true);
    setNotice("");
  }

  async function openEdit(article) {
    setCreating(false);
    setNotice("");
    try {
      const res = await api.get(`/knowledge/${article.id}`);
      const a = res.data;
      setForm({ title: a.title, content: a.content, category: a.category || "geral", tags: a.tags || "", active: a.active !== 0 });
      setSelected(article.id);
    } catch {}
  }

  function cancel() { setSelected(null); setCreating(false); setNotice(""); }

  async function save(e) {
    e.preventDefault();
    setSaving(true); setNotice("");
    try {
      if (creating) {
        await api.post("/knowledge", {
          title: form.title,
          content: form.content,
          category: form.category,
          tags: form.tags || null,
        });
        setNotice("Artigo criado com sucesso.");
        setCreating(false);
      } else {
        await api.patch(`/knowledge/${selected}`, {
          title: form.title,
          content: form.content,
          category: form.category,
          tags: form.tags || null,
          active: form.active ? 1 : 0,
        });
        setNotice("Artigo atualizado.");
        setSelected(null);
      }
      await load();
    } catch (err) {
      setNotice(err.response?.data?.error || "Erro ao salvar.");
    } finally { setSaving(false); }
  }

  async function deleteArticle(article) {
    if (!window.confirm(`Excluir o artigo "${article.title}"?`)) return;
    try {
      await api.delete(`/knowledge/${article.id}`);
      if (selected === article.id) setSelected(null);
      await load();
    } catch (err) {
      setNotice(err.response?.data?.error || "Erro ao excluir.");
    }
  }

  const isEditing = creating || selected !== null;

  return (
    <div className="knowledgePage">
      <div className="knowledgeHeader">
        <div>
          <h2>Base de Conhecimento</h2>
          <p>Artigos internos usados pela equipe e pela Safe IA para sugestões contextuais.</p>
        </div>
        {!isEditing && (
          <button onClick={openCreate}>
            <Icon name="plus" size={14} /> Novo artigo
          </button>
        )}
      </div>

      {notice && <div className="noticeBox">{notice}</div>}

      {isEditing ? (
        /* ── Editor ─────────────────────────────────────────── */
        <div className="knowledgeEditor">
          <div className="knowledgeEditorHeader">
            <strong>{creating ? "Novo artigo" : "Editar artigo"}</strong>
            <button className="ghostButton" onClick={cancel}>Cancelar</button>
          </div>
          <form onSubmit={save} className="knowledgeForm">
            <div className="formRow">
              <label>Título *</label>
              <input
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                placeholder="Título do artigo"
                required
              />
            </div>
            <div className="formRow2">
              <div>
                <label>Categoria</label>
                <select value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}>
                  {CATEGORY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div>
                <label>Tags (separadas por vírgula)</label>
                <input
                  value={form.tags}
                  onChange={(e) => setForm((f) => ({ ...f, tags: e.target.value }))}
                  placeholder="preço, entrega, garantia"
                />
              </div>
            </div>
            <div className="formRow">
              <label>Conteúdo *</label>
              <textarea
                rows={12}
                value={form.content}
                onChange={(e) => setForm((f) => ({ ...f, content: e.target.value }))}
                placeholder={"Escreva o conteúdo do artigo aqui.\n\nUse texto simples ou Markdown para organizar as informações."}
                required
              />
            </div>
            {!creating && (
              <div className="knowledgeToggleRow">
                <label>
                  <input
                    type="checkbox"
                    checked={form.active}
                    onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
                  />
                  Artigo ativo (visível para a equipe e para a Safe IA)
                </label>
              </div>
            )}
            <div className="formActions">
              <button type="submit" disabled={saving}>{saving ? "Salvando…" : "Salvar artigo"}</button>
              <button type="button" className="ghostButton" onClick={cancel}>Cancelar</button>
            </div>
          </form>
        </div>
      ) : (
        /* ── List ────────────────────────────────────────────── */
        <>
          <div className="knowledgeToolbar">
            <div className="knowledgeSearch">
              <Icon name="search" size={14} />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar artigos por título ou conteúdo…"
              />
              {search && <button className="clearBtn" onClick={() => setSearch("")}><Icon name="x" size={12} /></button>}
            </div>
            <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
              <option value="">Todas as categorias</option>
              {categories.map((c) => (
                <option key={c.category} value={c.category}>{c.category} ({c.count})</option>
              ))}
            </select>
          </div>

          {/* Category pills */}
          {categories.length > 0 && (
            <div className="knowledgeCategoryStrip">
              <button
                className={`knowledgeCatPill ${!categoryFilter ? "active" : ""}`}
                onClick={() => setCategoryFilter("")}
              >Todas</button>
              {categories.map((c) => (
                <button
                  key={c.category}
                  className={`knowledgeCatPill ${categoryFilter === c.category ? "active" : ""}`}
                  onClick={() => setCategoryFilter(c.category)}
                >
                  {c.category} <em>{c.count}</em>
                </button>
              ))}
            </div>
          )}

          {loading && <div className="loadingBox">Carregando artigos…</div>}

          {!loading && articles.length === 0 && (
            <div className="emptyKnowledge">
              <Icon name="rule" size={32} />
              <p>{search || categoryFilter ? "Nenhum artigo encontrado para esta busca." : "Nenhum artigo criado ainda."}</p>
              {!search && !categoryFilter && (
                <button onClick={openCreate}>Criar primeiro artigo</button>
              )}
            </div>
          )}

          <div className="knowledgeGrid">
            {articles.map((article) => (
              <div key={article.id} className="knowledgeCard">
                <div className="knowledgeCardHeader">
                  <div>
                    <strong>{article.title}</strong>
                    <div className="knowledgeCardMeta">
                      <CategoryBadge category={article.category} />
                      {article.tags && (
                        <span className="knowledgeTagList">
                          {article.tags.split(",").slice(0, 3).map((t, i) => (
                            <span key={i} className="knowledgeTag">{t.trim()}</span>
                          ))}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="knowledgeCardActions">
                    <button className="ghostButton xs" onClick={() => openEdit(article)} title="Editar">
                      <Icon name="edit" size={13} />
                    </button>
                    <button className="ghostButton xs danger" onClick={() => deleteArticle(article)} title="Excluir">
                      <Icon name="trash" size={13} />
                    </button>
                  </div>
                </div>
                <p className="knowledgeCardSnippet">
                  {(article.content || "").slice(0, 160)}{article.content?.length > 160 ? "…" : ""}
                </p>
                <div className="knowledgeCardFooter">
                  <small>Atualizado {formatTime(article.updated_at || article.created_at)}</small>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default KnowledgePage;
